import { writeFile, mkdir, readFile, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { homedir } from 'node:os'
import { execSync } from 'node:child_process'
import { randomBytes } from 'node:crypto'
import { TontyError, SetupError } from '../errors.js'
import { execSetup, deleteMatchingFiles } from '../workspace.js'
import type { DownloadedSession } from './client.js'
import type { SessionMetadata } from './server.js'
import type { ProgressCallback } from '../session/manager.js'
import { importRepo } from '../scenario/importer.js'

export const DEFAULT_WORKER_URL = 'https://tonty-tech-interviews-relay.tonty-tech-interviews.workers.dev'

export function getWorkerUrl(): string {
  return process.env['TONTY_WORKER_URL'] || DEFAULT_WORKER_URL
}

export class CloudError extends TontyError {
  constructor(message: string) {
    super(message, 'CLOUD_ERROR', 'Check your network connection and try again')
  }
}

export async function uploadSession(
  workerUrl: string,
  metadata: SessionMetadata,
  systemPrompt: string,
  tarballPath: string,
  onProgress?: ProgressCallback,
): Promise<{ code: string; expiresAt: string }> {
  onProgress?.('Reading workspace tarball...')
  const tarballData = await readFile(tarballPath)

  onProgress?.('Uploading to cloud...')
  const formData = new FormData()
  formData.set('metadata', JSON.stringify(metadata))
  formData.set('systemPrompt', systemPrompt)
  formData.set(
    'workspace',
    new Blob([tarballData], { type: 'application/gzip' }),
    'workspace.tar.gz',
  )

  let response: Response
  try {
    response = await fetch(`${workerUrl}/sessions`, {
      method: 'POST',
      body: formData,
    })
  } catch (err) {
    throw new CloudError(
      `Failed to connect to cloud relay at ${workerUrl}: ${err instanceof Error ? err.message : String(err)}`,
    )
  }

  if (!response.ok) {
    const body = await response.text().catch(() => 'unknown error')
    throw new CloudError(`Cloud upload failed (${response.status}): ${body}`)
  }

  const result = (await response.json()) as { code: string; expiresAt: string }
  return result
}

export async function downloadSessionFromCloud(
  workerUrl: string,
  code: string,
  targetDir?: string,
  onProgress?: ProgressCallback,
): Promise<DownloadedSession> {
  const id = randomBytes(4).toString('hex')

  let rawCode = code.trim().toUpperCase()
  if (rawCode.startsWith('TONTY-')) {
    rawCode = rawCode.slice(6)
  }
  rawCode = rawCode.toLowerCase()

  onProgress?.('Connecting to cloud relay...')
  const metadataRes = await cloudFetch(`${workerUrl}/sessions/${rawCode}/metadata`)
  const metadata = (await metadataRes.json()) as SessionMetadata

  onProgress?.('Downloading scenario...')
  const promptRes = await cloudFetch(`${workerUrl}/sessions/${rawCode}/system-prompt`)
  const systemPrompt = await promptRes.text()

  onProgress?.('Downloading workspace...')
  let tarball: Buffer | null = null
  try {
    const workspaceRes = await cloudFetch(`${workerUrl}/sessions/${rawCode}/workspace`)
    tarball = Buffer.from(await workspaceRes.arrayBuffer())
  } catch {
    // Workspace tarball not in KV (scenario too large). Fall back to
    // git-cloning the repo directly if the worker supplied repo + commit
    // in the session metadata.
    if ('repo' in metadata && 'commit' in metadata && metadata.repo && metadata.commit) {
      onProgress?.('Cloning repository (workspace too large for cloud)...')
    } else {
      throw new CloudError('Workspace not found and no repo info available')
    }
  }

  onProgress?.('Extracting workspace...')
  const workdir = targetDir ?? join(homedir(), 'tonty-sessions', `${metadata.scenarioName}-${id}`)
  await mkdir(workdir, { recursive: true })

  if (tarball) {
    const tarballPath = join(workdir, '..', `${id}-download.tar.gz`)
    await writeFile(tarballPath, tarball)
    execSync(`tar xzf "${tarballPath}" -C "${workdir}"`, { stdio: 'pipe' })
    // Clean up tarball
    try { execSync(`rm -f "${tarballPath}"`, { stdio: 'pipe' }) } catch { /* best-effort */ }
  } else {
    // Clone repo directly (KV size limit workaround for large scenarios).
    // Replay the same workspace setup that build-templates.mjs does, so
    // large-scenario candidates get the same complete workspace as small-scenario
    // candidates who receive the pre-built tarball.
    await importRepo(metadata.repo!, workdir, metadata.commit!)

    // Apply scenario patches (e.g., inject the bug, modify source files)
    if (metadata.patch) {
      for (const p of metadata.patch) {
        try {
          const content = await readFile(join(workdir, p.file), 'utf-8')
          await writeFile(join(workdir, p.file), content.replaceAll(p.find, p.replace))
        } catch {
          // Patch file may not exist in the cloned repo — skip
        }
      }
    }

    // BRIEFING.md so candidate can re-read the briefing inside their AI tool
    // when the terminal output is no longer visible.
    await writeFile(join(workdir, 'BRIEFING.md'), `# Interview Briefing\n\n${metadata.briefing}`)

    // Remove scenario.yaml that may have leaked from the upstream repo
    try { await rm(join(workdir, 'scenario.yaml')) } catch { /* best-effort */ }

    // Delete files specified in scenario config
    if (metadata.delete_files && metadata.delete_files.length > 0) {
      await deleteMatchingFiles(workdir, metadata.delete_files)
    }

    // Re-init git for a clean slate (no upstream history visible)
    try {
      try { await rm(join(workdir, '.git'), { recursive: true, force: true }) } catch { /* best-effort */ }
      execSync(
        'git init && git add -A && git -c user.name=tonty -c user.email=tonty@local commit -m "initial"',
        { cwd: workdir, stdio: 'ignore' },
      )
    } catch { /* git not available — skip */ }
  }

  // Always pull the scenario's current verify.sh from the relay and overwrite
  // any copy bundled in the tarball. The tarball may ship an older verify.sh
  // (e.g. GNU-only grep -Pzo, or --experimental-strip-types on Node 20), and
  // the clone-fallback path ships none at all — either way scoring would
  // false-FAIL a candidate who actually fixed the bug. The relay serves the
  // portable version (uploaded fresh from packages/scenarios), so joined
  // candidates are always scored against the current script.
  try {
    const verifyRes = await fetch(`${workerUrl}/sessions/${rawCode}/verify-sh`, {
      signal: AbortSignal.timeout(30000),
    })
    if (verifyRes.ok) {
      const verifyScript = await verifyRes.text()
      if (verifyScript.trim()) {
        await writeFile(join(workdir, 'verify.sh'), verifyScript)
        try { execSync('chmod +x verify.sh', { cwd: workdir, stdio: 'ignore' }) } catch { /* best-effort */ }
        onProgress?.('verify.sh ready')
      }
    }
  } catch {
    // Best-effort: fall back to whatever verify.sh came with the workspace
    // (tarball) — never fail the join because the verify fetch failed.
  }

  // Install dependencies. Templates strip node_modules to stay under the KV
  // size limit, and the clone-fallback path never ran setup — so both cases
  // replay setup here, matching the local `start` flow.
  if (metadata.setupCommands && metadata.setupCommands.length > 0) {
    onProgress?.('Installing dependencies...')
    for (const cmd of metadata.setupCommands) {
      const result = execSetup(cmd, workdir)
      if (!result.ok) {
        throw new SetupError(cmd, result.stderr)
      }
    }
  }

  const promptDir = join(homedir(), '.tonty-tech-interviews', 'prompts')
  await mkdir(promptDir, { recursive: true })
  const systemPromptPath = join(promptDir, `${id}.md`)
  await writeFile(systemPromptPath, systemPrompt)

  return { workdir, systemPromptPath, metadata, id }
}

async function cloudFetch(url: string): Promise<Response> {
  let response: Response
  try {
    response = await fetch(url, { signal: AbortSignal.timeout(60000) })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new CloudError('Cloud download timed out')
    }
    throw new CloudError(
      `Failed to connect to cloud relay: ${err instanceof Error ? err.message : String(err)}`,
    )
  }

  if (response.status === 404) {
    throw new CloudError('Session not found or expired')
  }

  if (!response.ok) {
    const body = await response.text().catch(() => 'unknown error')
    throw new CloudError(`Cloud request failed (${response.status}): ${body}`)
  }

  return response
}
